import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Skeleton,
  Button,
  Chip,
  Divider,
  useMediaQuery,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import { trpc } from '../../lib/trpc';
import { StatusPageMonitorRow } from '../../components/status-page';
import type { MonitorStatus as StatusPageMonitorStatus } from '../../components/status-page';
import { IncidentCard } from '../../components/status-page';
import type { IncidentData } from '../../components/status-page';

/**
 * Overall status type
 */
type OverallStatus = 'operational' | 'degraded' | 'down' | 'maintenance';

/**
 * Status configuration
 */
const overallStatusConfig: Record<
  OverallStatus,
  {
    label: string;
    color: string;
    backgroundColor: string;
    icon: React.ReactElement;
    description: string;
  }
> = {
  operational: {
    label: 'All Systems Operational',
    color: '#10B981',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
    description: 'All monitored services are running smoothly.',
  },
  degraded: {
    label: 'Degraded Performance',
    color: '#F59E0B',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    icon: <WarningIcon sx={{ fontSize: 32 }} />,
    description: 'Some services are experiencing issues.',
  },
  down: {
    label: 'Major Outage',
    color: '#EF4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    icon: <ErrorIcon sx={{ fontSize: 32 }} />,
    description: 'Some services are currently unavailable.',
  },
  maintenance: {
    label: 'Under Maintenance',
    color: '#6366F1',
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    icon: <BuildIcon sx={{ fontSize: 32 }} />,
    description: 'Scheduled maintenance is in progress.',
  },
};

/**
 * Loading skeleton for public status page
 */
function PublicStatusPageSkeleton() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Skeleton */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Skeleton variant="rectangular" width={200} height={50} sx={{ mx: 'auto', mb: 2 }} />
        <Skeleton variant="text" width="60%" height={48} sx={{ mx: 'auto' }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mx: 'auto' }} />
      </Box>

      {/* Status Banner Skeleton */}
      <Skeleton variant="rounded" height={100} sx={{ mb: 4, borderRadius: 2 }} />

      {/* Monitors Skeleton */}
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>

      {/* Incidents Skeleton */}
      <Box>
        <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    </Container>
  );
}

/**
 * 404 Not Found component
 */
function StatusPageNotFound() {
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        sx={{
          p: 6,
          textAlign: 'center',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography
          variant="h1"
          sx={{ fontSize: '4rem', fontWeight: 700, mb: 2, color: 'text.secondary' }}
        >
          404
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Status Page Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          The status page you are looking for does not exist or may have been removed.
        </Typography>
        <Button component="a" href="/" variant="outlined">
          Go to Homepage
        </Button>
      </Paper>
    </Container>
  );
}

/**
 * Overall Status Banner component
 */
function StatusBanner({ status, primaryColor }: { status: OverallStatus; primaryColor?: string }) {
  const config = overallStatusConfig[status];
  const bannerColor = status === 'operational' && primaryColor ? primaryColor : config.color;
  const backgroundColor =
    status === 'operational' && primaryColor ? `${primaryColor}15` : config.backgroundColor;

  return (
    <Paper
      sx={{
        p: 3,
        mb: 4,
        textAlign: 'center',
        backgroundColor,
        border: 2,
        borderColor: bannerColor,
        borderRadius: 2,
      }}
      elevation={0}
    >
      <Box sx={{ color: bannerColor, mb: 1 }}>{config.icon}</Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: bannerColor, mb: 0.5 }}>
        {config.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {config.description}
      </Typography>
    </Paper>
  );
}

/**
 * Public Status Page component
 * Displays the public-facing status page (no authentication required)
 */
export function PublicStatusPage() {
  const { slug } = useParams<{ slug: string }>();
  const isMobile = useMediaQuery('(max-width:600px)');

  // Fetch status page data (public endpoint - no auth)
  const {
    data: statusPage,
    isLoading,
    error,
    refetch,
    isFetching,
  } = trpc.statusPages.getBySlug.useQuery(
    { slug: slug! },
    {
      enabled: !!slug,
      retry: false,
      refetchInterval: 60000, // Refresh every minute
    }
  );

  // Create custom theme based on primary color
  const customTheme = useMemo(() => {
    const primaryColor = statusPage?.branding?.primaryColor || '#3B82F6';
    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
      },
    });
  }, [statusPage?.branding?.primaryColor]);

  // Update page title
  useEffect(() => {
    if (statusPage) {
      document.title = `${statusPage.name} - Status`;
    }
    return () => {
      document.title = 'SiteAwake';
    };
  }, [statusPage]);

  if (isLoading) {
    return <PublicStatusPageSkeleton />;
  }

  if (error || !statusPage) {
    return <StatusPageNotFound />;
  }

  const primaryColor = statusPage.branding?.primaryColor || '#3B82F6';

  // Convert incidents to IncidentData format
  const activeIncidents: IncidentData[] = statusPage.activeIncidents.map((incident) => ({
    id: incident.id,
    monitorId: incident.monitorId,
    monitorDisplayName: incident.monitorDisplayName,
    status: incident.status as 'ONGOING' | 'RESOLVED',
    cause: incident.cause,
    startedAt: incident.startedAt,
    resolvedAt: incident.resolvedAt,
    durationMs: incident.durationMs,
    isManual: incident.isManual,
    title: incident.title,
    message: incident.message,
  }));

  const recentIncidents: IncidentData[] = statusPage.recentIncidents.map((incident) => ({
    id: incident.id,
    monitorId: incident.monitorId,
    monitorDisplayName: incident.monitorDisplayName,
    status: incident.status as 'ONGOING' | 'RESOLVED',
    cause: incident.cause,
    startedAt: incident.startedAt,
    resolvedAt: incident.resolvedAt,
    durationMs: incident.durationMs,
    isManual: incident.isManual,
    title: incident.title,
    message: incident.message,
  }));

  // Get last update time
  const lastUpdated = new Date(statusPage.lastUpdated).toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  });

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#F9FAFB',
          pb: 4,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderBottom: 1,
            borderColor: 'divider',
            py: 3,
            mb: 4,
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              {/* Logo */}
              {statusPage.branding?.logoUrl && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={statusPage.branding.logoUrl}
                    alt={`${statusPage.name} logo`}
                    style={{
                      maxHeight: 50,
                      maxWidth: '100%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Box>
              )}

              {/* Title */}
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 1 }}>
                {statusPage.name}
              </Typography>

              {/* Description */}
              {statusPage.description && (
                <Typography variant="body1" color="text.secondary">
                  {statusPage.description}
                </Typography>
              )}
            </Box>
          </Container>
        </Box>

        <Container maxWidth="md">
          {/* Overall Status Banner */}
          <StatusBanner status={statusPage.overallStatus} primaryColor={primaryColor} />

          {/* Active Incidents */}
          {activeIncidents.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <WarningIcon color="error" />
                Active Incidents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {activeIncidents.map((incident) => (
                  <IncidentCard key={incident.id} incident={incident} primaryColor={primaryColor} />
                ))}
              </Box>
            </Box>
          )}

          {/* Monitors List */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Services
              </Typography>
              <Chip
                label={`Last updated: ${lastUpdated}`}
                size="small"
                variant="outlined"
                icon={
                  <RefreshIcon
                    sx={{
                      animation: isFetching ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                }
                onClick={() => refetch()}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {statusPage.monitors.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {statusPage.monitors.map((monitor) => (
                  <StatusPageMonitorRow
                    key={monitor.id}
                    monitor={{
                      id: monitor.id,
                      displayName: monitor.displayName,
                      status: monitor.status as StatusPageMonitorStatus,
                      uptimePercentage: monitor.uptimePercentage,
                      avgResponseTime: monitor.avgResponseTime,
                      lastCheckedAt: monitor.lastCheckedAt,
                      uptimeHistory: monitor.uptimeHistory,
                    }}
                    days={30}
                    primaryColor={primaryColor}
                    showDetails={true}
                  />
                ))}
              </Box>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No services are being monitored on this status page.
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Recent Resolved Incidents */}
          {recentIncidents.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Incidents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentIncidents.map((incident) => (
                  <IncidentCard key={incident.id} incident={incident} primaryColor={primaryColor} />
                ))}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Powered by{' '}
              <Box
                component="a"
                href="/"
                sx={{
                  color: primaryColor,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                SiteAwake
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default PublicStatusPage;
